import api from './index';
import type { Employee } from './Employee';
import type { Vehicle } from './vehicles';
import { resolvePublicFileUrl } from '../config.api';

export type CheckinStatus = 'in_progress' | 'finished';

export interface Checkin {
  id: number;
  vehicle_id: number;
  employee_id: number;
  date: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  images: CheckinImage[];
  trip_id?: number;
  status: CheckinStatus;
  employee?: Employee;
  vehicle?: Vehicle;
}

export interface CheckinImage {
  id: number;
  base64: string | null;
  url?: string | null;
  storage_key?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  created_at: string;
  type: 'CHECKIN' | 'CHECKOUT';
}

export function getCheckinImageSrc(img: CheckinImage | null | undefined): string {
  if (!img) return '';
  if (typeof img.url === 'string' && img.url.trim().length > 0) {
    return resolvePublicFileUrl(img.url);
  }
  const base64 = (typeof img.base64 === 'string' ? img.base64 : '').trim();
  if (!base64) return '';
  return base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
}

export interface CreateCheckinData {
  vehicle_id?: number;
  employee_id?: number;
  date?: string;
  notes?: string;
  checkinImage?: string;
  checkoutImage?: string;
  checkinId?: number;
  /**
   * Optional: when provided together with checkoutImage and final_mileage,
   * the backend will finalize this linked trip inside the same transaction
   * (unified check-out + trip finalization flow).
   */
  trip_id?: number;
  /**
   * Final odometer reading required to finalize the linked trip.
   * Only used when trip_id and checkoutImage are also provided.
   */
  final_mileage?: number;
}

export type UpdateCheckinData = Partial<CreateCheckinData>;

export interface CheckinHistory {
  id: number;
  checkin_id: number;
  action: string;
  details?: string;
  created_at: string;
  checkin: {
    id: number;
    date: string;
    notes?: string;
    duration?: number;
    status: CheckinStatus;
    images: CheckinImage[];
    employee: Pick<Employee, 'name' | 'cpf'>;
    vehicle: Pick<Vehicle, 'description' | 'license_plate'> & {
      trips?: { destination?: string; start_date?: string }[];
    };
  };
}

const baseUrl = '/checkin';

export const checkinService = {
  async getAll(): Promise<Checkin[]> {
    const response = await api.get(baseUrl);
    return response.data;
  },
  async create(data: CreateCheckinData): Promise<Checkin> {
    const response = await api.post(baseUrl, data);
    return response.data;
  },
  async update(id: number, data: UpdateCheckinData): Promise<Checkin> {
    const response = await api.put(`${baseUrl}/${id}`, data);
    return response.data;
  },
  async delete(id: number): Promise<void> {
    await api.delete(`${baseUrl}/${id}`);
  },
  async archiveCheckin(id: number, duration?: number): Promise<void> {
    await api.delete(`${baseUrl}/${id}`, { data: { duration } });
  },
  async getHistory(): Promise<CheckinHistory[]> {
    const response = await api.get(`${baseUrl}/history`);
    return response.data;
  },
};
